import { useState, useEffect, useCallback, useRef } from 'react';

interface TransactionStatus {
  id: string;
  status: string;
  actionsAvailable?: boolean;
  fromCurrency?: string;
  fromNetwork?: string;
  toCurrency?: string;
  toNetwork?: string;
  expectedAmountFrom?: number | string | null;
  expectedAmountTo?: number | string | null;
  amountFrom?: number | string | null;
  amountTo?: number | string | null;
  payoutAddress?: string;
  payinAddress?: string;
  payinExtraId?: string | null;
  payoutExtraId?: string | null;
  refundAddress?: string | null;
  refundExtraId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  depositReceivedAt?: string | null;
  payinHash?: string | null;
  payoutHash?: string | null;
  refundHash?: string | null;
  refundAmount?: number | string | null;
  validUntil?: string | null;
  relatedExchangesInfo?: unknown;
  repeatedExchangesInfo?: unknown;
  originalExchangeInfo?: unknown;
  // Compatibility with normalized provider responses used by other backends.
  fromAmount?: number | string;
  toAmount?: number | string;
  depositReceived?: boolean;
  payoutSent?: boolean;
  transactions?: unknown;
  changeNowData?: any;
}

interface UseTransactionStatusOptions {
  refreshInterval?: number; // in milliseconds, default 10000 (10 seconds)
  autoRefresh?: boolean; // default true
  maxRetries?: number; // default 120 (20 minutes at 10-second intervals)
}

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
const TERMINAL_STATUSES = new Set(['finished', 'completed', 'done', 'failed', 'refunded', 'expired']);
const PENDING_STATUSES = new Set(['new', 'waiting', 'confirming', 'verifying', 'exchanging', 'sending']);

export const useTransactionStatus = (
  transactionId: string | null,
  options: UseTransactionStatusOptions = {}
) => {
  const {
    refreshInterval = 10000, // 10 seconds default
    autoRefresh = true,
    maxRetries = 120 // 20 minutes maximum
  } = options;

  const [status, setStatus] = useState<TransactionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(autoRefresh);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);
  const retryCountRef = useRef(0);

  const fetchStatus = useCallback(async (
    skipCache: boolean = false
  ): Promise<boolean> => {
    if (!transactionId) {
      setError('Transaction ID is required');
      return false;
    }

    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    const controller = new AbortController();
    const requestVersion = ++requestVersionRef.current;
    activeRequestRef.current = controller;
    setIsLoading(true);
    setError(null);

    const isCurrentRequest = () => (
      requestVersionRef.current === requestVersion && activeRequestRef.current === controller
    );

    try {
      console.log(`🔍 [useTransactionStatus] Fetching status for ${transactionId}`);

      const response = await fetch(`${API_URL}/status/${transactionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      if (!isCurrentRequest()) return false;

      if (!response.ok) {
        throw new Error(`Status request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!isCurrentRequest()) return false;

      if (data.success) {
        setStatus(data.data);
        retryCountRef.current = 0;
        setRetryCount(0); // Reset retry count on successful fetch

        console.log(`✅ [useTransactionStatus] Status updated:`, {
          id: data.data.id,
          status: data.data.status,
          depositReceived: data.data.depositReceived,
          payoutSent: data.data.payoutSent
        });

        // Normalize status to lowercase for comparison
        const normalizedStatus = (data.data.status || '').toLowerCase();

        const isTerminal = TERMINAL_STATUSES.has(normalizedStatus);
        if (isTerminal) {
          setIsAutoRefreshEnabled(false);
          console.log(`🛑 [useTransactionStatus] Transaction ${normalizedStatus}, stopping auto-refresh`);
        }
        return !isTerminal;
      } else {
        throw new Error(data.error || 'Failed to fetch transaction status');
      }

    } catch (error) {
      if (!isCurrentRequest() || (error instanceof Error && error.name === 'AbortError')) {
        console.log('Status request aborted due to new request');
        return false;
      }

      console.error('❌ [useTransactionStatus] Error fetching status:', error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(errorMessage);

      // Keep retry bookkeeping out of fetchStatus's dependencies. Depending on
      // retryCount recreates this callback and makes the interval effect issue
      // another immediate request after every failure.
      const nextRetryCount = retryCountRef.current + 1;
      retryCountRef.current = nextRetryCount;
      setRetryCount(nextRetryCount);

      // Stop auto-refresh if max retries reached
      if (nextRetryCount >= maxRetries) {
        setIsAutoRefreshEnabled(false);
        console.log(`🛑 [useTransactionStatus] Max retries (${maxRetries}) reached, stopping auto-refresh`);
        return false;
      }

      return true;

    } finally {
      if (isCurrentRequest()) {
        activeRequestRef.current = null;
        setIsLoading(false);
      }
    }
  }, [transactionId, maxRetries]);

  // Manual refresh
  const refreshStatus = useCallback(() => {
    fetchStatus(true);
  }, [fetchStatus]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    setIsAutoRefreshEnabled(enabled);
  }, []);

  // A completed/failed transaction disables polling. Reset all per-transaction
  // state when a new ID is supplied so the next exchange starts polling normally.
  useEffect(() => {
    requestVersionRef.current += 1;
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    retryCountRef.current = 0;
    setRetryCount(0);
    setStatus(null);
    setError(null);
    setIsAutoRefreshEnabled(autoRefresh);
  }, [transactionId, autoRefresh]);

  // Schedule the next poll only after the current request settles. This keeps
  // slow provider responses from being repeatedly aborted by a fixed interval.
  useEffect(() => {
    let cancelled = false;

    if (isAutoRefreshEnabled && transactionId) {
      console.log(`🔄 [useTransactionStatus] Auto-refresh enabled: ${refreshInterval}ms interval`);

      const poll = async () => {
        const shouldContinue = await fetchStatus(true);
        if (!cancelled && shouldContinue) {
          pollTimeoutRef.current = setTimeout(poll, refreshInterval);
        }
      };
      void poll();
    }

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [isAutoRefreshEnabled, transactionId, refreshInterval, fetchStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      requestVersionRef.current += 1;
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
        activeRequestRef.current = null;
      }
    };
  }, []);

  // Status helper functions
  const normalizedStatus = status ? (status.status || '').toLowerCase() : '';
  const isPending = status && PENDING_STATUSES.has(normalizedStatus);
  const isCompleted = status && ['finished', 'completed', 'done'].includes(normalizedStatus);
  const isFailed = status && normalizedStatus === 'failed';
  const isRefunded = status && normalizedStatus === 'refunded';
  const needsDeposit = status && !status.depositReceived && ['new', 'waiting'].includes(normalizedStatus);

  return {
    status,
    isLoading,
    error,
    retryCount,
    isAutoRefreshEnabled,
    refreshStatus,
    toggleAutoRefresh,
    // Helper booleans
    isPending,
    isCompleted,
    isFailed,
    isRefunded,
    needsDeposit
  };
};
